import { statusLabel } from './webcheckinStatus';

/**
 * One colour per web check-in status, for every style system the screens use.
 * Hidden failures (FAILED) are orange, failures shown to the client
 * (FAILED_BY_ADMIN) red, so the two can be told apart at a glance.
 */
const PALETTE = {
  completed: { mui: 'success', name: 'green' },
  in_progress: { mui: 'primary', name: 'blue' },
  scheduled: { mui: 'info', name: 'yellow' },
  failed: { mui: 'warning', name: 'orange' },
  failed_by_admin: { mui: 'error', name: 'red' },
  rejected: { mui: 'secondary', name: 'purple' },
  cancelled: { mui: 'default', name: 'gray' }
};

// Ticket statuses and old spellings that share a colour.
const ALIASES = { confirmed: 'completed', checked_in: 'completed', pending: 'in_progress' };

// Full class names (not built from parts) so Tailwind keeps them.
const CLASSES = {
  green: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', dot: 'bg-green-500', menuText: 'text-green-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200', dot: 'bg-blue-500', menuText: 'text-blue-600' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', dot: 'bg-yellow-500', menuText: 'text-yellow-600' },
  orange: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200', dot: 'bg-orange-500', menuText: 'text-orange-600' },
  red: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', dot: 'bg-red-500', menuText: 'text-red-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200', dot: 'bg-purple-500', menuText: 'text-purple-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-800', border: 'border-gray-200', dot: 'bg-gray-500', menuText: 'text-gray-600' }
};

const paletteFor = (status) => {
  const key = String(status || '').trim().toLowerCase().replace(/[-\s]/g, '_');
  return PALETTE[ALIASES[key] || key] || { mui: 'default', name: 'gray' };
};
const classesFor = (status) => CLASSES[paletteFor(status).name];

/**
 * Get the color for a status badge
 * @param {string} status - The check-in status
 * @returns {object} Object containing Material UI color values
 */
export const getStatusColor = (status) => ({ color: paletteFor(status).mui, variant: 'outlined' });

/**
 * Get a human-readable status label
 * @param {string} status - The check-in status
 * @returns {string} Human-readable status
 */
export const getStatusLabel = (status) => statusLabel(status);

/**
 * Filter check-ins by status
 * @param {Array} checkIns - Array of check-in objects
 * @param {string} status - Status to filter by (or 'all')
 * @returns {Array} Filtered check-ins
 */
export const filterByStatus = (checkIns, status) => {
  if (!status || status === 'all') {
    return checkIns;
  }
  return checkIns.filter(checkIn => checkIn.status === status);
};

/**
 * Count check-ins by status
 * @param {Array} checkIns - Array of check-in objects
 * @returns {object} Object with counts for each status
 */
export const countByStatus = (checkIns) => {
  return checkIns.reduce((counts, checkIn) => {
    const status = checkIn.status || 'unknown';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, { all: checkIns.length });
};

/**
 * Get standardized Tailwind CSS classes for check-in status
 * @param {string} status - The check-in status
 * @returns {string} Tailwind CSS classes for background, text, and border
 */
export const getStatusTailwindClasses = (status) => {
  const { bg, text, border } = classesFor(status);
  return `${bg} ${text} ${border}`;
};

/**
 * Get standardized status styles for Radix UI components
 * @param {string} status - The check-in status
 * @returns {object} Object containing bgColor, textColor, and borderColor classes
 */
export const getStatusRadixStyles = (status) => {
  const { bg, text, border } = classesFor(status);
  return { bgColor: bg, textColor: text, borderColor: border };
};

/**
 * Get standardized Bootstrap/CSS class names for status
 * @param {string} status - The check-in status
 * @returns {string} Bootstrap class name (success, warning, danger, info, secondary)
 */
export const getStatusBootstrapClass = (status) =>
  ({ success: 'success', primary: 'info', info: 'warning', warning: 'warning', error: 'danger', secondary: 'secondary' })[
    paletteFor(status).mui
  ] || 'info';

/**
 * Get status indicator dot color classes for dropdown menus
 * @param {string} status - The check-in status
 * @returns {string} Tailwind background color class for status indicator dot
 */
export const getStatusIndicatorColor = (status) => classesFor(status).dot;

/**
 * Get status text color classes for dropdown menus
 * @param {string} status - The check-in status
 * @returns {string} Tailwind text color class for status text
 */
export const getStatusTextColor = (status) => classesFor(status).menuText;
